import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useMemo, useRef, useCallback, useEffect } from 'react';
import { deprecate, uniqueId, safeRest } from '@wds/utils';
import { Label } from '@wds/label';
import { createMemoStyles, getColor } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    radius = theme.radius,
    shadow = theme.shadow,
    type = theme.type;
  var textarea = {
    fontSize: type.fontSize.body,
    paddingSm: spacing[2],
    paddingMd: spacing[3],
    paddingLg: spacing[4],
    borderRadius: radius.small
  };
  return {
    wrenchTextarea: {
      boxSizing: 'border-box',
      width: function (_a) {
        var stretched = _a.stretched;
        return stretched ? '100%' : null;
      },
      maxHeight: function (_a) {
        var maxHeight = _a.maxHeight;
        return maxHeight;
      },
      padding: function (_a) {
        var size = _a.size;
        if (size === 'sm') return textarea.paddingSm;
        if (size === 'lg') return textarea.paddingLg;
        return textarea.paddingMd; // default to md
      },
      margin: 0,
      border: function (_a) {
        var color = _a.color;
        return "solid 1px ".concat(getColor(palette, color, {
          fallback: palette.border.main
        }));
      },
      borderRadius: textarea.borderRadius,
      color: palette.text.dark,
      fontFamily: type.fontFamily.base,
      outline: 'none',
      '-webkit-appearance': 'none',
      '-webkit-font-smoothing': 'antialiased',
      '-moz-appearance': 'none',
      '-ms-appearance': 'none',
      appearance: 'none',
      boxShadow: 'none',
      backgroundColor: palette.background.light,
      fontSize: textarea.fontSize,
      pointerEvents: function (_a) {
        var disabled = _a.disabled;
        return disabled ? 'none' : 'auto';
      },
      opacity: function (_a) {
        var disabled = _a.disabled;
        return disabled ? '0.4' : '1';
      },
      resize: function (_a) {
        var resizable = _a.resizable,
          expanding = _a.expanding;
        return resizable && !expanding ? null : 'none';
      },
      '&:hover': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.hover
          });
        },
        boxShadow: shadow.sky
      },
      '&:focus': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.active
          });
        }
      }
    }
  };
});

var Textarea = function Textarea(_a) {
  var color = _a.color,
    label = _a.label,
    id = _a.id;
    _a.ariaLabel;
    var _b = _a.stretched,
    stretched = _b === void 0 ? false : _b,
    _c = _a.size,
    size = _c === void 0 ? 'md' : _c,
    _d = _a.disabled,
    disabled = _d === void 0 ? false : _d,
    _e = _a.resizable,
    resizable = _e === void 0 ? true : _e,
    _f = _a.expanding,
    expanding = _f === void 0 ? false : _f,
    maxHeight = _a.maxHeight,
    rest = __rest(_a, ["color", "label", "id", "ariaLabel", "stretched", "size", "disabled", "resizable", "expanding", "maxHeight"]);
  deprecate({
    color: color
  }, 'color', 'warning', 'Textarea');
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('textarea');
  }, [id]);
  var textareaRef = useRef(null);
  var wrenchTextarea = useStyles({
    color: color,
    size: size,
    stretched: stretched,
    disabled: disabled,
    resizable: resizable,
    expanding: expanding,
    maxHeight: maxHeight
  }).wrenchTextarea;
  var handleInput = useCallback(function () {
    if (textareaRef.current && expanding) {
      textareaRef.current.style.height = '';
      if (maxHeight) {
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, maxHeight) + 'px';
      } else {
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }
  }, [textareaRef, expanding, maxHeight]);
  useEffect(function () {
    handleInput();
  }, [handleInput, rest.value]);
  return jsxs(Fragment, {
    children: [label && jsx(Label, {
      htmlFor: inputID,
      children: label
    }), jsx("textarea", __assign({
      ref: textareaRef,
      className: wrenchTextarea,
      id: inputID,
      disabled: disabled,
      onChange: function () {
        return handleInput();
      }
    }, safeRest(rest)))]
  });
};

export { Textarea };
